import React, { useContext, useState } from 'react';
import Modal from '../common/Modal';
import Select from '../common/Select';
import { useForm, useFormState } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Errors from '../common/Errors';
import {
  createCalendarEvent,
  destroyCalendarEvent,
  updateCalendarEvent,
  downloadICSFile,
} from './constants/handleCalendarEvents';
import { OptionsContext } from '../../hooks/Contexts';
import Icon from '../common/Icon';
import dayjs from 'dayjs';
import { notify } from '../../helpers/notifications';
import { downloadBlob } from '../../helpers/downloadBlob';

const defaultCalendarValues = {
  summary: '',
  description: '',
  location: '',
  dtstart: '',
  dtend: '',
  alert_trigger: '',
  status: 'CONFIRMED',
  timezone: 'America/Chicago',
};

var utc = require('dayjs/plugin/utc');
var tz = require('dayjs/plugin/timezone');
dayjs.extend(utc);
dayjs.extend(tz);
// Convert UTC times from database to the specified zone,
// so the datetime input appears independent from the zone.
const toLocalTime = (time, zone) => {
  return dayjs.utc(time).tz(zone).format().substring(0, 16);
};

const CalendarEventModal = ({
  showCalendarModal,
  toggleCalendarModal,
  option,
}) => {
  const [calendarEvent, setCalendarEvent] = useState(option?.calendar_event);
  const { options, refreshOption } = useContext(OptionsContext);
  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: calendarEvent
      ? {
          ...calendarEvent,
          dtstart: toLocalTime(calendarEvent.dtstart, calendarEvent.timezone),
          dtend: toLocalTime(calendarEvent.dtend, calendarEvent.timezone),
        }
      : defaultCalendarValues,
    criteriaMode: 'all',
  });
  const { dirtyFields } = useFormState({ control });

  const handleSave = async (formData) => {
    if (Object.keys(dirtyFields).length === 0) return toggleCalendarModal();
    let calEvent = { ...formData, option_id: option.id };
    const response = calendarEvent
      ? await updateCalendarEvent(calendarEvent.id, calEvent)
      : await createCalendarEvent(calEvent);

    if (response.ok) {
      refreshOption(options, option.id, {
        ...option,
        calendar_event: response.data,
      });
      toggleCalendarModal();
    } else if (response.errors) {
      Object.entries(response.errors).forEach(([key, message]) =>
        setError(key, { types: { [key]: message } })
      );
    }
  };

  const handleDelete = async () => {
    let id = calendarEvent.id;
    if (id)
      await destroyCalendarEvent(id, (res) => {
        if (res.ok) {
          const { calendar_event, ...optionKeys } = option;
          refreshOption(options, option.id, optionKeys);
          setCalendarEvent(null);
          reset(defaultCalendarValues);
          toggleCalendarModal();
        }
      });
  };

  const handleDownloadEvent = async () => {
    if (Object.keys(dirtyFields).length > 0) {
      return notify.info('Save edits before downloading!');
    }
    const ics = await downloadICSFile(calendarEvent.id);
    if (ics.ok) {
      let filename = `${calendarEvent.summary}.ics`;
      downloadBlob(new Blob([ics.data]), filename);
    }
  };

  return (
    <Modal
      title='fieldset-creation-modal'
      show={showCalendarModal}
      onClose={toggleCalendarModal}
    >
      <header>
        <h3>Manage iCal Data</h3>
      </header>
      <div
        className='creation-form'
        style={{ maxHeight: '600px', overflowY: 'scroll', paddingTop: '0' }}
      >
        <h4>{option.name}</h4>

        <form onSubmit={handleSubmit(handleSave)}>
          <div className='row'>
            <label htmlFor='ical-uid'>Calendar Event UID:</label>
            <p style={{ margin: '0' }}>{calendarEvent?.uid || 'None'}</p>
          </div>
          <div className='row'>
            <label htmlFor='ical-summary'>Summary:</label>
            <ErrorMessage
              errors={errors}
              name='summary'
              render={({ messages }) => <Errors messages={messages} />}
            />
            <input
              id='ical-summary'
              placeholder='Summary / Name of event...'
              {...register('summary', { required: 'Required' })}
            />
          </div>
          <div className='row'>
            <label htmlFor='ical-description'>Description:</label>
            <ErrorMessage
              errors={errors}
              name='description'
              render={({ messages }) => <Errors messages={messages} />}
            />
            <textarea
              maxLength={255}
              id='ical-description'
              placeholder='Event details, URL links, Organizer info, etc... Max: 255 chars'
              {...register('description', { required: 'Required' })}
            />
          </div>
          <div className='row'>
            <label htmlFor='ical-location'>Location (optional):</label>
            <ErrorMessage
              errors={errors}
              name='location'
              render={({ messages }) => <Errors messages={messages} />}
            />
            <input
              id='ical-location'
              {...register('location')}
              placeholder='Optional...'
            />
          </div>
          <div className='row'>
            <label htmlFor='ical-dtstart'>DateTime Start:</label>
            <ErrorMessage
              errors={errors}
              name='dtstart'
              render={({ messages }) => <Errors messages={messages} />}
            />
            <input
              type='datetime-local'
              id='ical-dtstart'
              {...register('dtstart', {
                required: 'Required',
                valueAsDate: true,
              })}
            />
          </div>
          <div className='row'>
            <label htmlFor='ical-dtend'>DateTime End:</label>
            <ErrorMessage
              errors={errors}
              name='dtend'
              render={({ messages }) => <Errors messages={messages} />}
            />
            <input
              type='datetime-local'
              id='ical-dtend'
              {...register('dtend', {
                required: 'Required',
                valueAsDate: true,
              })}
            />
          </div>
          <div className='row'>
            <label htmlFor='ical-alert'>Alert Trigger (optional):</label>
            <p>
              <i>
                Format: 1-2 digits followed by D (day) / H (hour) / M (minutes)
              </i>
            </p>
            <ErrorMessage
              errors={errors}
              name='alert_trigger'
              render={({ messages }) => <Errors messages={messages} />}
            />
            <input
              id='ical-alert'
              placeholder='Ex: 7D'
              {...register('alert_trigger', {
                pattern: {
                  value: /[1-9][0-9]{0,1}[DMH]/,
                  message: 'Invalid pattern',
                },
                maxLength: { value: 3, message: 'Value exceeds max length' },
              })}
            />
          </div>
          <div className='row'>
            <label htmlFor='ical-timezone'>Timezone:</label>
            <ErrorMessage
              errors={errors}
              name='timezone'
              render={({ messages }) => <Errors messages={messages} />}
            />
            <Select
              content={[
                { value: 'America/Chicago', label: 'America/Chicago' },
                { value: 'America/Denver', label: 'America/Denver' },
              ]}
              register={{ id: 'ical-timezone', ...register('timezone') }}
            />
          </div>
          <div className='row'>
            <label htmlFor='ical-status'>Event Status:</label>
            <ErrorMessage
              errors={errors}
              name='status'
              render={({ messages }) => <Errors messages={messages} />}
            />
            <Select
              content={[
                { value: 'CONFIRMED', label: 'CONFIRMED' },
                { value: 'CANCELLED', label: 'CANCELLED' },
              ]}
              register={{ id: 'ical-status', ...register('status') }}
            />
          </div>

          <div className='controls'>
            <button type='submit'>Save & Close</button>
            <button
              className='blue right'
              onClick={(e) => {
                e.preventDefault();
                toggleCalendarModal();
              }}
            >
              Cancel
              <Icon icon='xmark' />
            </button>
            {calendarEvent?.id && (
              <>
                <Icon
                  icon='download'
                  title='Download ICS file'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadEvent();
                  }}
                />
                <button
                  className='danger right'
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete();
                  }}
                >
                  Delete
                  <Icon icon='trash' />
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CalendarEventModal;
